import { type DocumentNode, Kind } from "graphql";
import {
  AccordionByIdsDocument,
  type ContentPageSectionsItem,
  ContentSliderByIdsDocument,
  GeometryTableByIdsDocument,
  ProductListingByIdsDocument,
  SectionClaimByIdsDocument,
  SectionCounterByIdsDocument,
  SectionGalleryByIdsDocument,
  SectionHeroByIdsDocument,
  SectionHotspotByIdsDocument,
  SectionIntroTextByIdsDocument,
  SectionMediaByIdsDocument,
  SectionQuoteByIdsDocument,
  SectionSliderByIdsDocument,
  SectionTaglineByIdsDocument,
  SectionTeaserByIdsDocument,
  SectionTextByIdsDocument,
  SectionTextImageByIdsDocument,
  TabContainerByIdsDocument,
} from "@/services/contentful/generated/contentful-sdk.generated";

export type SectionTypename = NonNullable<
  ContentPageSectionsItem["__typename"]
>;

const SECTION_DOCUMENTS = {
  AccordionD2C: AccordionByIdsDocument,
  ContentSliderD2C: ContentSliderByIdsDocument,
  GeometryTable: GeometryTableByIdsDocument,
  Hotspot: SectionHotspotByIdsDocument,
  ProductListing: ProductListingByIdsDocument,
  SectionClaim: SectionClaimByIdsDocument,
  SectionCounter: SectionCounterByIdsDocument,
  SectionGallery: SectionGalleryByIdsDocument,
  SectionHero: SectionHeroByIdsDocument,
  SectionIntroText: SectionIntroTextByIdsDocument,
  SectionMedia: SectionMediaByIdsDocument,
  SectionQuote: SectionQuoteByIdsDocument,
  SectionSlider: SectionSliderByIdsDocument,
  SectionTagline: SectionTaglineByIdsDocument,
  SectionTeaser: SectionTeaserByIdsDocument,
  SectionText: SectionTextByIdsDocument,
  SectionTextImage: SectionTextImageByIdsDocument,
  TabContainerD2C: TabContainerByIdsDocument,
} satisfies Record<SectionTypename, DocumentNode>;

/**
 * Page queries only select section references, but Contentful live preview
 * needs the fragments of every section rendered on the page to apply editor
 * updates. Merges the page document with all section documents, keeping each
 * fragment definition once.
 */
export function withSectionDocuments(document: DocumentNode): DocumentNode {
  const fragmentNames = new Set<string>();

  const definitions = [document, ...Object.values(SECTION_DOCUMENTS)]
    .flatMap((doc) => doc.definitions)
    .filter((definition) => {
      if (definition.kind !== Kind.FRAGMENT_DEFINITION) {
        return true;
      }
      if (fragmentNames.has(definition.name.value)) {
        return false;
      }
      fragmentNames.add(definition.name.value);
      return true;
    });

  return { kind: Kind.DOCUMENT, definitions };
}
